import { formatCurrency as formatMoney } from '@/lib/utils';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  ArrowRight,
  CheckCircle2,
  Clock,
  Eye,
  Calendar,
  Info,
  EyeOff,
  Ban,
  Undo2,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';

const ShiftsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [excludedShifts, setExcludedShifts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  
  // Initialize from URL params or default to 'all'
  const [selectedBusiness, setSelectedBusiness] = useState(searchParams.get('business') || 'all');
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'pending');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncFromDate, setSyncFromDate] = useState('');
  const [syncInfo, setSyncInfo] = useState(null);
  const [minSyncDate, setMinSyncDate] = useState('2026-01-26');
  
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('closed_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const isAdmin = user?.role === 'admin';

  const fetchSyncInfo = async () => {
    try {
      const res = await api.get('/shifts/sync-info');
      setSyncInfo(res.data.businesses);
      setMinSyncDate(res.data.min_sync_date);
      if (!syncFromDate) {
        setSyncFromDate(res.data.min_sync_date);
      }
    } catch (error) {
      console.error('Failed to fetch sync info');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setSelectedIds(new Set()); // Clear selection on data fetch
    try {
      if (viewMode === 'excluded') {
        const [excludedRes, businessesRes] = await Promise.all([
          api.get('/shifts/excluded'),
          api.get('/api-keys/all')
        ]);
        setExcludedShifts(excludedRes.data);
        setShifts([]);
        setBusinesses(businessesRes.data);
      } else {
        const isReconciled = viewMode === 'reconciled';
        const [shiftsRes, businessesRes] = await Promise.all([
          api.get('/shifts', { params: { is_reconciled: isReconciled } }),
          api.get('/api-keys/all')
        ]);
        setShifts(shiftsRes.data);
        setExcludedShifts([]);
        setBusinesses(businessesRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSyncInfo();
    setCurrentPage(1); // Reset to first page on view change
  }, [viewMode]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedBusiness !== 'all') {
      params.set('business', selectedBusiness);
    }
    if (viewMode !== 'pending') {
      params.set('view', viewMode);
    }
    setSearchParams(params, { replace: true });
  }, [selectedBusiness, viewMode, setSearchParams]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBusiness, itemsPerPage]);

  const handleExcludeShift = async (shiftId, isCurrentlyExcluded) => {
    try {
      const response = await api.put(`/shifts/${shiftId}/exclude`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to update shift');
    }
  };

  const handleUnreconcile = async (shiftId) => {
    try {
      const cashupRes = await api.get(`/cashups/shift/${shiftId}`);
      if (cashupRes.data && cashupRes.data.id) {
        await api.post(`/cashups/${cashupRes.data.id}/unreconcile`);
        toast.success('Shift reverted to pending');
        fetchData();
      } else {
        toast.error('No cashup found for this shift');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unreconcile shift');
    }
  };

  // Bulk action handlers
  const handleBulkExclude = async () => {
    setBulkActionLoading(true);
    try {
      const response = await api.post('/shifts/bulk-exclude', {
        shift_ids: Array.from(selectedIds)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to exclude shifts');
    } finally {
      setBulkActionLoading(false);
      setShowBulkConfirm(false);
    }
  };

  const handleBulkInclude = async () => {
    setBulkActionLoading(true);
    try {
      const response = await api.post('/shifts/bulk-include', {
        shift_ids: Array.from(selectedIds)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to include shifts');
    } finally {
      setBulkActionLoading(false);
      setShowBulkConfirm(false);
    }
  };

  const handleBulkUnreconcile = async () => {
    setBulkActionLoading(true);
    try {
      const response = await api.post('/shifts/bulk-unreconcile', {
        shift_ids: Array.from(selectedIds)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert shifts');
    } finally {
      setBulkActionLoading(false);
      setShowBulkConfirm(false);
    }
  };

  const openBulkConfirm = (action) => {
    setBulkAction(action);
    setShowBulkConfirm(true);
  };

  const executeBulkAction = () => {
    if (bulkAction === 'exclude') handleBulkExclude();
    else if (bulkAction === 'include') handleBulkInclude();
    else if (bulkAction === 'unreconcile') handleBulkUnreconcile();
  };

  const getBulkActionText = () => {
    const count = selectedIds.size;
    if (bulkAction === 'exclude') return `Exclude ${count} shift(s)?`;
    if (bulkAction === 'include') return `Include ${count} shift(s)?`;
    if (bulkAction === 'unreconcile') return `Revert ${count} shift(s) to pending?`;
    return '';
  };

  const getBulkActionDescription = () => {
    if (bulkAction === 'exclude') return 'These shifts will be hidden from the pending list. You can restore them later from the Excluded tab.';
    if (bulkAction === 'include') return 'These shifts will be restored and moved back to the pending list.';
    if (bulkAction === 'unreconcile') return 'These shifts will be unlocked and moved back to pending state. All associated ledger entries will be removed.';
    return '';
  };

  const openSyncDialog = () => {
    fetchSyncInfo();
    setShowSyncDialog(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    setShowSyncDialog(false);
    try {
      const response = await api.post('/shifts/sync', {
        sync_from_date: syncFromDate || null
      });
      toast.success(`Synced ${response.data.total_synced} new shifts`);
      if (response.data.errors?.length) {
        response.data.errors.forEach(err => toast.error(err));
      }
      if (response.data.last_sync_info) {
        const newSyncInfo = Object.values(response.data.last_sync_info);
        setSyncInfo(newSyncInfo);
      }
      fetchData();
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  const formatSyncDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const getMostRecentSync = () => {
    if (!syncInfo || syncInfo.length === 0) return null;
    const sorted = [...syncInfo].filter(s => s.last_sync).sort((a, b) => 
      new Date(b.last_sync) - new Date(a.last_sync)
    );
    return sorted[0] || null;
  };

  const displayShifts = viewMode === 'excluded' ? excludedShifts : shifts;
  
  // Filter by business
  const businessFilteredShifts = selectedBusiness === 'all' 
    ? displayShifts 
    : displayShifts.filter(s => s.business_id === selectedBusiness);

  // Sorting logic
  const sortedShifts = useMemo(() => {
    const sorted = [...businessFilteredShifts].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'business_name':
          aVal = a.business_name?.toLowerCase() || '';
          bVal = b.business_name?.toLowerCase() || '';
          break;
        case 'store_name':
          aVal = a.store_name?.toLowerCase() || '';
          bVal = b.store_name?.toLowerCase() || '';
          break;
        case 'closed_at':
          aVal = new Date(a.closed_at || 0);
          bVal = new Date(b.closed_at || 0);
          break;
        case 'cash_payments':
          aVal = (a.cash_payments || 0) - (a.cash_refunds || 0);
          bVal = (b.cash_payments || 0) - (b.cash_refunds || 0);
          break;
        case 'card_payments':
          aVal = (a.card_payments || 0) - (a.card_refunds || 0);
          bVal = (b.card_payments || 0) - (b.card_refunds || 0);
          break;
        case 'eft_payments':
          aVal = (a.eft_payments || 0) - (a.eft_refunds || 0);
          bVal = (b.eft_payments || 0) - (b.eft_refunds || 0);
          break;
        case 'status':
          aVal = a.is_reconciled ? 2 : (a.is_excluded ? 0 : 1);
          bVal = b.is_reconciled ? 2 : (b.is_excluded ? 0 : 1);
          break;
        default:
          aVal = a[sortColumn] || '';
          bVal = b[sortColumn] || '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [businessFilteredShifts, sortColumn, sortDirection]);

  // Pagination logic
  const totalPages = Math.ceil(sortedShifts.length / itemsPerPage);
  const paginatedShifts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedShifts.slice(start, start + itemsPerPage);
  }, [sortedShifts, currentPage, itemsPerPage]);

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(paginatedShifts.map(s => s.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (shiftId, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(shiftId);
    } else {
      newSelected.delete(shiftId);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = paginatedShifts.length > 0 && paginatedShifts.every(s => selectedIds.has(s.id));
  const isSomeSelected = paginatedShifts.some(s => selectedIds.has(s.id)) && !isAllSelected;

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-[#006241]" />
      : <ArrowDown className="h-4 w-4 text-[#006241]" />;
  };

  const formatCurrency = (amount) => formatMoney(amount);

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="page-container animate-slide-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Shifts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {viewMode === 'pending' 
              ? 'Review and confirm cashups for pending shifts'
              : viewMode === 'excluded'
              ? 'Manage excluded shifts (admin only)'
              : 'View completed reconciliations'}
          </p>
        </div>
        {viewMode === 'pending' && (
          <div className="flex items-center gap-3">
            {getMostRecentSync() && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Last sync: {formatSyncDate(getMostRecentSync()?.last_sync)}
              </span>
            )}
            <Button 
              onClick={openSyncDialog} 
              disabled={syncing}
              className="bg-[#006241] hover:bg-[#004d33]"
              data-testid="sync-shifts-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync from Loyverse'}
            </Button>
          </div>
        )}
      </div>

      {/* Sync Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[#006241]" />
              Sync from Loyverse
            </DialogTitle>
            <DialogDescription>
              Fetch new and updated shifts from Loyverse. Existing shifts will not be duplicated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sync-from-date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Sync From Date
              </Label>
              <Input
                id="sync-from-date"
                type="date"
                value={syncFromDate}
                min={minSyncDate}
                onChange={(e) => setSyncFromDate(e.target.value)}
                data-testid="sync-from-date"
              />
              <p className="text-xs text-muted-foreground">
                Only shifts closed after this date will be synced (minimum: {minSyncDate})
              </p>
            </div>

            {syncInfo && syncInfo.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Last Sync Per Business
                </Label>
                <div className="bg-[#F2F2F0] rounded-lg p-3 space-y-2">
                  {syncInfo.map((business) => (
                    <div key={business.id} className="flex justify-between text-sm">
                      <span className="font-medium">{business.business_name}</span>
                      <span className="text-muted-foreground">
                        {formatSyncDate(business.last_sync)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowSyncDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSync}
              className="bg-[#006241] hover:bg-[#004d33]"
              data-testid="confirm-sync-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Start Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getBulkActionText()}</AlertDialogTitle>
            <AlertDialogDescription>
              {getBulkActionDescription()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeBulkAction}
              disabled={bulkActionLoading}
              className={
                bulkAction === 'exclude' ? 'bg-red-500 hover:bg-red-600' :
                bulkAction === 'unreconcile' ? 'bg-orange-500 hover:bg-orange-600' :
                'bg-green-500 hover:bg-green-600'
              }
            >
              {bulkActionLoading ? 'Processing...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Toggle */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="tab-pending">
            <Clock className="h-4 w-4" />
            Pending
          </TabsTrigger>
          <TabsTrigger value="reconciled" className="flex items-center gap-2" data-testid="tab-reconciled">
            <CheckCircle2 className="h-4 w-4" />
            Reconciled
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="excluded" className="flex items-center gap-2" data-testid="tab-excluded">
              <EyeOff className="h-4 w-4" />
              Excluded
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {/* Filters and Bulk Actions Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-4">
          <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
            <SelectTrigger className="w-[250px]" data-testid="business-filter">
              <SelectValue placeholder="Filter by business" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {businesses.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {sortedShifts.length} {viewMode === 'excluded' ? 'excluded' : viewMode === 'pending' ? 'pending' : 'reconciled'} shift{sortedShifts.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Bulk Action Buttons */}
        {selectedIds.size > 0 && isAdmin && (
          <div className="flex items-center gap-2 bg-[#F2F2F0] rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-[#1A1A18]">{selectedIds.size} selected</span>
            {viewMode === 'pending' && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => openBulkConfirm('exclude')}
                data-testid="bulk-exclude-btn"
              >
                <Ban className="h-4 w-4 mr-1" />
                Exclude
              </Button>
            )}
            {viewMode === 'reconciled' && (
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
                onClick={() => openBulkConfirm('unreconcile')}
                data-testid="bulk-unreconcile-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Revert to Pending
              </Button>
            )}
            {viewMode === 'excluded' && (
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => openBulkConfirm('include')}
                data-testid="bulk-include-btn"
              >
                <Undo2 className="h-4 w-4 mr-1" />
                Include
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Shifts Table */}
      <Card>
        <CardContent className="p-0">
          {sortedShifts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              {viewMode === 'excluded' ? (
                <EyeOff className="h-12 w-12 text-gray-400 mb-4" />
              ) : (
                <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              )}
              <h3 className="text-lg font-medium text-[#1A1A18]">
                {viewMode === 'pending' ? 'All caught up!' : viewMode === 'excluded' ? 'No excluded shifts' : 'No reconciled shifts'}
              </h3>
              <p className="text-muted-foreground mt-1">
                {viewMode === 'pending' 
                  ? 'No pending shifts to reconcile'
                  : viewMode === 'excluded'
                  ? 'No shifts have been excluded'
                  : 'No shifts have been reconciled yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F2F2F0]">
                      {isAdmin && (
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={handleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                      )}
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('business_name')}
                      >
                        <div className="flex items-center gap-2">
                          Business
                          <SortIcon column="business_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('store_name')}
                      >
                        <div className="flex items-center gap-2">
                          Store / POS
                          <SortIcon column="store_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('closed_at')}
                      >
                        <div className="flex items-center gap-2">
                          Closed At
                          <SortIcon column="closed_at" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('cash_payments')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Cash
                          <SortIcon column="cash_payments" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('card_payments')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Card
                          <SortIcon column="card_payments" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('eft_payments')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          EFT
                          <SortIcon column="eft_payments" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-2">
                          Status
                          <SortIcon column="status" />
                        </div>
                      </TableHead>
                      {viewMode === 'reconciled' && <TableHead>Reconciled By</TableHead>}
                      {viewMode === 'excluded' && <TableHead>Excluded By</TableHead>}
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedShifts.map((shift) => (
                      <TableRow 
                        key={shift.id} 
                        className={`cursor-pointer hover:bg-muted/50 ${shift.is_excluded ? 'opacity-60' : ''} ${selectedIds.has(shift.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => viewMode !== 'excluded' && navigate(`/cashup/${shift.id}?returnBusiness=${selectedBusiness}&returnView=${viewMode}`)}
                        data-testid={`shift-row-${shift.id}`}
                      >
                        {isAdmin && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(shift.id)}
                              onCheckedChange={(checked) => handleSelectOne(shift.id, checked)}
                              data-testid={`select-shift-${shift.id}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">{shift.business_name}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{shift.store_name}</p>
                            <p className="text-xs text-muted-foreground">{shift.pos_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{formatDateTime(shift.closed_at)}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency((shift.cash_payments || 0) - (shift.cash_refunds || 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency((shift.card_payments || 0) - (shift.card_refunds || 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency((shift.eft_payments || 0) - (shift.eft_refunds || 0))}
                        </TableCell>
                        <TableCell>
                          {shift.is_excluded ? (
                            <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">
                              Excluded
                            </Badge>
                          ) : shift.is_reconciled ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              Reconciled
                            </Badge>
                          ) : shift.status === 'draft' ? (
                            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                              Draft
                            </Badge>
                          ) : shift.status === 'confirmed' ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              Confirmed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        {viewMode === 'reconciled' && (
                          <TableCell className="text-sm text-muted-foreground">
                            {shift.reconciled_by || '-'}
                          </TableCell>
                        )}
                        {viewMode === 'excluded' && (
                          <TableCell className="text-sm text-muted-foreground">
                            {shift.excluded_by || '-'}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {viewMode === 'excluded' ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Include shift"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExcludeShift(shift.id, true);
                                }}
                                data-testid={`include-shift-${shift.id}`}
                              >
                                <Undo2 className="h-4 w-4 text-green-600" />
                              </Button>
                            ) : (
                              <>
                                {isAdmin && viewMode === 'pending' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    title="Exclude shift"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExcludeShift(shift.id, false);
                                    }}
                                    data-testid={`exclude-shift-${shift.id}`}
                                  >
                                    <Ban className="h-4 w-4 text-red-500" />
                                  </Button>
                                )}
                                {isAdmin && viewMode === 'reconciled' && (
                                  <AlertDialog>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      title="Revert to Pending"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnreconcile(shift.id);
                                      }}
                                      data-testid={`unreconcile-shift-${shift.id}`}
                                    >
                                      <RotateCcw className="h-4 w-4 text-orange-500" />
                                    </Button>
                                  </AlertDialog>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  title={viewMode === 'reconciled' ? 'View' : 'Reconcile'}
                                >
                                  {viewMode === 'reconciled' ? (
                                    <Eye className="h-4 w-4" />
                                  ) : (
                                    <ArrowRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Show</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(val) => setItemsPerPage(parseInt(val))}>
                    <SelectTrigger className="w-[80px]" data-testid="items-per-page">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="75">75</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">per page</span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      data-testid="first-page-btn"
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      data-testid="last-page-btn"
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftsPage;
